import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';

import {environment} from '@env/environment';
import {
    ALLOWED_FEATURE_TOGGLE_SCHEMA_MAP,
    ALLOWED_ONLY_FOR_SSO_APPS_FEATURE_TOGGLES_MAP,
    FEATURE_TOGGLE_LEVEL,
    FEATURE_TOGGLE_PERMISSIONS,
    FEATURE_TOGGLE_STATUSES,
    FeatureToggleModel,
    SHOW_DISTRICT_FEATURE_TOGGLE_ON_DISTRICT_APPLICATION_LEVEL,
    XAPI_GRADE_SYNC
} from '@app/views/common/feature-toggles/featue-toggle.model';
import {DefaultSchemaName} from '@app/views/admin/application-settings/models/data-source-config.model';
import {ApplicationProviderListModel} from '@app/views/admin/application-settings/models/application-provider-list.model';
import {DataSourceType} from '@app/views/admin/application-settings/models/data-source-type.model';

@Injectable()

export class FeatureTogglesService {

    saveFeatureToggles$ = new BehaviorSubject<boolean>(false);

    static filterAllowedFeatureTogglesList(
        featureTogglesList: FeatureToggleModel[],
        permission: FEATURE_TOGGLE_PERMISSIONS,
        defaultSchemaName?: DefaultSchemaName,
        featureLevel?: FEATURE_TOGGLE_LEVEL
    ) {
        return (featureTogglesList || [])
            .filter((featureToggle: FeatureToggleModel) => {

                const hideDistrictFeatureToggleForSysAdmin: boolean = (
                    featureToggle.level.includes(FEATURE_TOGGLE_LEVEL.DISTRICT)
                    && SHOW_DISTRICT_FEATURE_TOGGLE_ON_DISTRICT_APPLICATION_LEVEL.includes(featureToggle.keyName)
                );
                const isVisible = featureToggle.visible
                    || ((permission === FEATURE_TOGGLE_PERMISSIONS.SYS_ADMIN) && !hideDistrictFeatureToggleForSysAdmin);

                const isAllowedStatus = (
                    (featureToggle.status !== FEATURE_TOGGLE_STATUSES.EXPERIMENTAL)
                    && (featureToggle.status !== FEATURE_TOGGLE_STATUSES.READY)
                );

                const isAllowedByPredefinedSchema = this.getIsAllowedByPredefinedSchema(defaultSchemaName, featureToggle);
                const isAllowedByFeatureToggleLevel = this.getIsAllowedByFeatureToggleLevel(featureToggle, featureLevel);

                return isVisible
                    && isAllowedStatus
                    && isAllowedByPredefinedSchema
                    && isAllowedByFeatureToggleLevel;
            })
            .map((featureToggle: FeatureToggleModel) => {
                featureToggle.disabled = !featureToggle.permissions.includes(permission);
                return featureToggle;
            });
    }

    static filterSpecificFeatureToggleDisableStatus(
        featureTogglesList: FeatureToggleModel[],
        providerApplications: ApplicationProviderListModel[]
    ): FeatureToggleModel[] {
        return (featureTogglesList || []).map((featureToggle: FeatureToggleModel) => {
            if (!featureToggle.disabled) {
                if (featureToggle.keyName === XAPI_GRADE_SYNC) {
                    featureToggle.disabled = providerApplications.every((app: ApplicationProviderListModel) =>
                        app.dataSourceType !== DataSourceType.LTI
                    );
                }
            }
            return featureToggle;
        });
    }

    private static getIsAllowedByPredefinedSchema(defaultSchemaName: DefaultSchemaName, featureToggle: FeatureToggleModel): boolean {
        let isAllowedByPredefinedSchema = !defaultSchemaName;
        if (!isAllowedByPredefinedSchema) {
            isAllowedByPredefinedSchema = !ALLOWED_FEATURE_TOGGLE_SCHEMA_MAP.get(featureToggle.keyName)
                || ALLOWED_FEATURE_TOGGLE_SCHEMA_MAP.get(featureToggle.keyName).includes(defaultSchemaName);

            if (featureToggle?.schemas?.length > 0) {
                isAllowedByPredefinedSchema = featureToggle.schemas.includes(defaultSchemaName);
            }
        }
        return isAllowedByPredefinedSchema;
    }

    private static getIsAllowedByFeatureToggleLevel(featureToggle: FeatureToggleModel, featureLevel?: FEATURE_TOGGLE_LEVEL): boolean {
        return !featureLevel
            || featureToggle.level.includes(featureLevel)
            || ((featureLevel === FEATURE_TOGGLE_LEVEL.SSO_APPLICATION)
                && (ALLOWED_ONLY_FOR_SSO_APPS_FEATURE_TOGGLES_MAP.get(featureToggle.keyName)));
    }

    constructor(
        private http: HttpClient) {
    }

    getDistrictFeatureToggles(districtGuid: string): Observable<FeatureToggleModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/feature_toggles/district/${districtGuid}`;

        return this.http
            .get<FeatureToggleModel[]>(url)
            .pipe(catchError(this.handleError));
    }

    getVendorFeatureToggles(vendorGuid: string): Observable<FeatureToggleModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/feature_toggles/vendor/${vendorGuid}`;

        return this.http
            .get<FeatureToggleModel[]>(url)
            .pipe(catchError(this.handleError));
    }

    getDistrictFeatureTogglesByDefaultApp(districtGuid: string, defaultAppGuid: string): Observable<FeatureToggleModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/feature_toggles/district/${districtGuid}/${defaultAppGuid}`;

        return this.http
            .get<FeatureToggleModel[]>(url)
            .pipe(catchError(this.handleError));
    }

    getDistrictFeatureTogglesOnlyForApp(districtGuid: string, defaultAppGuid: string): Observable<FeatureToggleModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/feature_toggles/district/${districtGuid}/onlyForApp/${defaultAppGuid}`;

        return this.http
            .get<FeatureToggleModel[]>(url)
            .pipe(catchError(this.handleError));
    }

    getApplicationFeatureToggles(
        appGuid: string, defaultAppGuid: string, featureLevel: FEATURE_TOGGLE_LEVEL
    ): Observable<FeatureToggleModel[]> {

        let url: string;
        if (appGuid) {
            url = `${environment.datahubBaseURI}/api/v2/feature_toggles/applications/${appGuid}/${defaultAppGuid}?level=${featureLevel}`;
        } else {
            url = `${environment.datahubBaseURI}/api/v2/feature_toggles/applications/${defaultAppGuid}?level=${featureLevel}`;
        }
        return this.http
            .get<FeatureToggleModel[]>(url)
            .pipe(catchError(this.handleError));
    }

    getIntegrationFeatureToggles(
        integrationId: string, defaultAppGuid: string, featureLevel: FEATURE_TOGGLE_LEVEL = FEATURE_TOGGLE_LEVEL.DISTRICT_VENDOR_INTEGRATION
    ): Observable<FeatureToggleModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/feature_toggles/integrations/${integrationId}/${defaultAppGuid}?level=${featureLevel}`;

        return this.http
            .get<FeatureToggleModel[]>(url)
            .pipe(catchError(this.handleError));
    }

    activateDistrictFeatureToggles(districtGuid: string, featureToggles: Object): Observable<string> {
        const url = `${environment.datahubBaseURI}/api/v2/feature_toggles/district/apply/${districtGuid}`;

        return this.http
            .post(url, featureToggles, {responseType: 'text' as const})
            .pipe(catchError(this.handleError));
    }

    activateDistrictFeatureToggleCourseraXAPI(districtGuid: string, isActive: boolean): Observable<string> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/coursera/configuration/xApi/${districtGuid}/activation?isActive=${isActive}`;

        return this.http
            .post(url, null, {responseType: 'text' as const})
            .pipe(catchError(this.handleError));
    }

    activateVendorFeatureToggles(vendorGuid: string, featureToggles: Object): Observable<string> {
        const url = `${environment.datahubBaseURI}/api/v2/feature_toggles/vendor/apply/${vendorGuid}`;

        return this.http
            .post(url, featureToggles, {responseType: 'text' as const})
            .pipe(catchError(this.handleError));
    }

    activateApplicationFeatureToggles(appGuid: string, orgGuid: string, featureToggles: Object): Observable<string> {
        const url = `${environment.datahubBaseURI}/api/v2/feature_toggles/applications/apply/${appGuid}/${orgGuid}`;

        return this.http
            .post(url, featureToggles, {responseType: 'text' as const})
            .pipe(catchError(this.handleError));
    }

    activateIntegrationFeatureToggles(integrationId: string, orgGuid: string, featureToggles: Object): Observable<string> {
        const url = `${environment.datahubBaseURI}/api/v2/feature_toggles/integrations/apply/${integrationId}/${orgGuid}`;

        return this.http
            .post(url, featureToggles, {responseType: 'text' as const})
            .pipe(catchError(this.handleError));
    }

    getRetentionPeriodSettings(districtGuid: string): Observable<number> {
        const url = `${environment.datahubBaseURI}/api/v2/roster-data/retention-period/${districtGuid}`;

        return this.http.get<number>(url);
    }

    saveCustomRetentionPeriodSettings(districtGuid: string, customRetentionPeriod: number): Observable<string> {
        const url = `${environment.datahubBaseURI}/api/v2/roster-data/retention-period/${districtGuid}`;

        const headers = new HttpHeaders();
        headers.append('Content-Type', 'application/json;charset=UTF-8');
        headers.append('Content-Length', '3');

        return this.http
            .post(url, customRetentionPeriod, {headers: headers, responseType: 'text' as const});
    }

    deleteCustomRetentionPeriodSettings(districtGuid: string): Observable<any> {
        const url = `${environment.datahubBaseURI}/api/v2/roster-data/retention-period/${districtGuid}`;
        return this.http
            .delete(url);
    }

    getSystemFeatureToggles(): Observable<FeatureToggleModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/feature_toggles`;

        return this.http
            .get<FeatureToggleModel[]>(url);
    }

    updateSystemFeatureToggles(updatedFeatureToggles: FeatureToggleModel): Observable<FeatureToggleModel> {
        const url = `${environment.datahubBaseURI}/api/v2/feature_toggles`;

        return this.http
            .put<FeatureToggleModel>(url, updatedFeatureToggles)
            .pipe(catchError(this.handleError));
    }

    getFeatureToggleStatus(featureToggles: FeatureToggleModel[], keyName: string): boolean {
        return (featureToggles || []).some((featureToggle: FeatureToggleModel) =>
            featureToggle.keyName === keyName && featureToggle.enabled
        );
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            console.error('An error occurred:', error.error.message);
        } else {
            console.error(
                `Backend returned ${error.status}, ${error.error}`);
        }
        return throwError(
            'Something bad happened, please try again later.');
    }
}
